import {
  EnvironmentOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { Spin, Typography } from "antd";
import Select from "../../../components/generic/Select";
import { useTenantBranches } from "../../../hooks/useTenantBranches";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTenantTopics } from "../../../hooks/useTenantTopics";
import Loading from "../../../components/generic/Loading";
import { useDispatch, useSelector } from "react-redux";
import { createConversation } from "../../../store/chats/thunks";
import { setKeyValue } from "../../../store/chats/slice";
import { setKeyValue as setUserKeyValue } from "../../../store/user/slice";
import CustomDetails from "../../../components/generic/CustomDetails";
import { selectUser } from "../../../store/user/selectors";
import { FILE_BASE_URL } from "../../../config";
import BackButton from "../../../components/shared/BackButton";

const TenantDetails = ({ selectedTenant }) => {
  const [branchId, setBranchId] = useState();
  const [topicId, setTopicId] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { branchOptions, branches, loading } = useTenantBranches();
  const { topicsOptions, topics } = useTenantTopics({
    selectedBranchId: branchId,
  });
  const user = useSelector(selectUser);

  const dispatch = useDispatch();

  useEffect(() => {
    setBranchId(null);
    setTopicId(null);
  }, [selectedTenant?.id]);

  useEffect(() => {
    const mainBranch = branches?.find(({ is_main_branch }) => is_main_branch);
    if (mainBranch) setBranchId(mainBranch?.id);
  }, [branches]);

  const selectedBranch = useMemo(() => {
    return branches?.find(({ id }) => branchId === id) || null;
  }, [branches, branchId]);

  const onSubmit = useCallback(
    async (topic_id) => {
      try {
        const values = {
          tenant_id: selectedTenant?.id,
          branch_id: branchId,
          topic_id,
        };
        setTopicId(topic_id);
        setIsSubmitting(true);
        const { payload } = await dispatch(
          createConversation({ ...values, type: "create" }),
        );

        const topic = topics.find(({ id }) => values?.topic_id === id);
        const conversation_id = payload?.data?.[0]?.id;

        if (conversation_id) {
          dispatch(
            setKeyValue({
              key: "topicQuestions",
              value: {
                ...topic,
                conversation_id,
                questions: [
                  {
                    id: `${Date.now()}`,
                    type: "string",
                    order: 0,
                    question: `Hello ${user?.first_name || "Customer"}, welcome to ${selectedTenant?.name}! How can I assist you today?`,
                  },
                  ...(topic?.questions || []),
                ],
              },
            }),
          );
        }
      } catch (error) {
        console.log("🚀 ~ HomeView.jsx ~ HomeView ~ error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, topics, topicId, branchId],
  );

  return (
    <div className="tenant-details">
      <div className="flex align-center justify-space-between ">
        <div className="flex align-center tenant-header">
          <BackButton
            className="d-none tenant-flow-back"
            onClick={() =>
              dispatch(
                setUserKeyValue({ key: "selectedTenantId", value: null }),
              )
            }
          />
          <div className="tenant-avatar flex-center">
            {selectedTenant?.logo ? (
              <img src={`${FILE_BASE_URL}${selectedTenant?.logo}`} />
            ) : (
              <ShopOutlined style={{ fontSize: 28, color: "#a3a3a3" }} />
            )}
          </div>
          <div className="flex-col">
            <Typography.Title level={4} className="tenant-title">
              {selectedTenant?.name}
            </Typography.Title>
            {selectedTenant.domain && (
              <div className="contact-item">
                <GlobalOutlined />
                <a
                  href={`https://${selectedTenant.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedTenant.domain}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
      <div>
        <CustomDetails
          title="Branch Details"
          titleClassname="section-title"
          open
        >
          {loading ? (
            <Loading />
          ) : (
            <>
              <Select
                className="w-100"
                placeholder="Select Business Branch"
                options={branchOptions}
                value={branchId}
                loading={loading}
                disabled={isSubmitting}
                onChange={setBranchId}
              />
              {selectedBranch && (
                <>
                  <div className="branch-details">
                    <div className="branch-detail-item">
                      <EnvironmentOutlined className="branch-detail-icon" />
                      <span className="branch-detail-text">
                        {selectedBranch.address}, {selectedBranch.city} -
                        {selectedBranch.postal_code}
                      </span>
                    </div>
                    <div className="branch-detail-item">
                      <PhoneOutlined className="branch-detail-icon" />
                      <a
                        href={`tel:${selectedBranch.mobile_number}`}
                        className="branch-detail-text"
                      >
                        {selectedBranch.mobile_number}
                      </a>
                    </div>
                    <div className="branch-detail-item">
                      <MailOutlined className="branch-detail-icon" />
                      <a
                        href={`mailto:${selectedBranch.email}`}
                        className="branch-detail-text"
                      >
                        {selectedBranch.email}
                      </a>
                    </div>
                  </div>
                  <div>
                    <div className="section-title">Topics</div>
                    <div className="topics-container hide-scrollbar">
                      {topicsOptions.map(({ label, value }) => (
                        <div
                          key={value}
                          className={`capitalize-text topic-chip ${
                            value === topicId ? "active" : ""
                          } ${isSubmitting ? "disabled" : ""}`}
                          onClick={() => {
                            if (isSubmitting) return;
                            onSubmit(value);
                          }}
                        >
                          {isSubmitting && topicId === value && (
                            <>
                              <Spin size="small" /> &nbsp;
                            </>
                          )}
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CustomDetails>
        <hr />
      </div>
    </div>
  );
};

export default TenantDetails;
